import {
  CbhIcon,
  List,
  ListItem,
  ListItemText,
  PageHeaderWrapper,
  Text,
  Title,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Alert, Box, Stack, ThemeProvider } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { convertToGeoLocation } from "@src/appV2/Location";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { getGradientByTimeSlot } from "@src/appV2/redesign/Shift/getGradientByTimeSlot";
import { ShiftHighlightsAndMapListItem } from "@src/appV2/redesign/Shift/HighlightsAndMapListItem";
import { ShiftWorkplaceListItem } from "@src/appV2/redesign/Shift/WorkplaceListItem";
import { SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { SHIFT_DETAILS_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { type ShiftPeriod, shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { generatePath, useHistory, useParams } from "react-router-dom";

import { PageContent } from "../../components/PageContent";
import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { ParkingCard } from "../components/ParkingCard";

export function LocationTrackingFailedPage() {
  const { shiftId } = useParams<{ shiftId: string }>();
  const { userId } = useDefinedWorker();
  const history = useHistory();

  const { shift, shiftState, isLoading, shiftError } = useGetShiftDetailsData({ shiftId });

  useLogEffect(
    APP_V2_USER_EVENTS.SHIFT_DETAILS_LOCATION_TRACKING_FAILED_PAGE_VIEWED,
    { shiftId, workerId: userId },
    { enabled: isDefined(shiftId) && isDefined(userId) }
  );

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PageHeaderWrapper sx={{ paddingTop: 0 }}>
          <Stack
            sx={(theme) => ({
              ...getSafeAreaPaddings({ top: true, fallbackTop: theme.spacing(3) }),
              paddingBottom: 7,
              paddingX: 6,
              transition: theme.transitions.create(["background"]),
              background: isDefined(shift?.name)
                ? getGradientByTimeSlot({
                    timeSlot: shiftPeriodToTimeSlot[shift.name as ShiftPeriod],
                    theme,
                  })
                : undefined,
            })}
          >
            <Stack spacing={6}>
              <IconButton
                iconType="chevron-left"
                variant="outlined"
                aria-label="Go back"
                onClick={() => {
                  history.push(generatePath(SHIFT_DETAILS_FULL_PATH, { shiftId }));
                }}
              />
              <Title component="h3" variant="h3">
                Location tracking failed
              </Title>
            </Stack>
          </Stack>
        </PageHeaderWrapper>

        <PageContent>
          {isDefined(shiftError) && (
            <Alert severity="error" sx={{ margin: 4 }}>
              Something went wrong while loading shift details. Please try again.
            </Alert>
          )}

          {!isLoading && isDefined(shift) && isDefined(shiftState) && (
            <List>
              <ListItem divider>
                <ListItemText
                  disableTypography
                  primary={
                    <Stack direction="row" spacing={4} alignItems="flex-start">
                      <CbhIcon
                        type="triangle-exclamation"
                        size="medium"
                        color={(theme) => theme.palette.error.main}
                      />
                      <Stack spacing={2}>
                        <Text semibold variant="h6">
                          Location tracking issue
                        </Text>
                        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                          We were unable to track your location. Please check your device settings
                          and make sure location services are enabled.
                        </Text>
                      </Stack>
                    </Stack>
                  }
                />
              </ListItem>

              <ListItem divider>
                <ListItemText
                  disableTypography
                  primary={
                    <Box sx={{ "& .MuiCard-root": { width: "100%" } }}>
                      <ParkingCard shift={shift} workerId={userId} shiftState={shiftState} />
                    </Box>
                  }
                />
              </ListItem>

              <ShiftWorkplaceListItem
                workplaceId={shift.facilityId}
                workplaceName={shift.facility.name}
                workplaceType={shift.facility.type ?? ""}
                workplaceLocation={convertToGeoLocation(shift.facility.geoLocation.coordinates)}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_WORKPLACE_CARD_TAPPED, {
                    shiftId: shift._id,
                    workplaceId: shift.facilityId,
                    sourceUI: "location-tracking-failed-page",
                  });

                  history.push(
                    generatePath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_FULL_PATH, {
                      workplaceId: shift.facilityId,
                    })
                  );
                }}
              />
              <ShiftHighlightsAndMapListItem
                isMapEnabled
                workplaceId={shift.facilityId}
                workplaceName={shift.facility.name}
                workplaceLocation={convertToGeoLocation(shift.facility.geoLocation.coordinates)}
                workplaceFormattedAddress={shift.facility.fullAddress?.formatted}
                isDividerEnabled={false}
              />
            </List>
          )}
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}
